export const TENANT_UPLOADED_FONTS_STYLE_TAG_ID = 'tenant-uploaded-fonts';
export const UPLOADED_FONT_MEDIA_BASE_URL = 'https://media.usestoryteller.com';

export interface UploadedFontCssSource {
  title?: string | null;
  family?: string | null;
  url?: string | null;
  fontMetrics?: string | null;
}

const uploadedFontsStyleSources = new Map<string, UploadedFontCssSource[]>();

const getStyleTag = () =>
  document.querySelector<HTMLStyleElement>(
    `style#${TENANT_UPLOADED_FONTS_STYLE_TAG_ID}`
  );

const getFontFormat = (url: string) => {
  const urlWithoutQuery = url.split('?')[0].toLowerCase();

  if (urlWithoutQuery.endsWith('.woff2')) {
    return 'woff2';
  }

  if (urlWithoutQuery.endsWith('.woff')) {
    return 'woff';
  }

  if (urlWithoutQuery.endsWith('.ttf')) {
    return 'truetype';
  }

  return 'opentype';
};

const escapeCssString = (value: string) =>
  value.replace(/\\/g, '\\\\').replace(/'/g, "\\'").replace(/\r?\n/g, '\\a ');

const getFontUrl = (url: string) => {
  const trimmedUrl = url.trim();

  if (/^(https?:)?\/\//.test(trimmedUrl) || trimmedUrl.startsWith('data:')) {
    return trimmedUrl;
  }

  if (trimmedUrl.startsWith('/fonts/card-preview/')) {
    return trimmedUrl;
  }

  const normalizedUrl = trimmedUrl.replace(/^\/+/, '');

  if (normalizedUrl.startsWith('fonts/')) {
    return `${UPLOADED_FONT_MEDIA_BASE_URL}/${normalizedUrl}`;
  }

  return `${UPLOADED_FONT_MEDIA_BASE_URL}/fonts/${normalizedUrl}`;
};

export const buildUploadedFontsCss = (
  ...fontGroups: UploadedFontCssSource[][]
) =>
  fontGroups
    .flat()
    .map(font => {
      const title = font.title?.trim() || font.family?.trim();
      const url = font.url?.trim();

      if (!title || !url) {
        return '';
      }

      const fontUrl = getFontUrl(url);
      const escapedTitle = escapeCssString(title);
      const escapedUrl = escapeCssString(fontUrl);

      return `
@font-face {
  font-family: '${escapedTitle}';
  font-display: swap;
  font-style: normal;
  font-weight: normal;
  src: url('${escapedUrl}') format('${getFontFormat(fontUrl)}');
}`;
    })
    .filter(Boolean)
    .join('\n');

export const syncUploadedFontsStyleTag = (
  ...fontGroups: Array<UploadedFontCssSource[] | null | undefined>
) => {
  const fonts = fontGroups.flatMap(fontGroup => fontGroup || []);
  const css = buildUploadedFontsCss(fonts);
  const existingStyleTag = getStyleTag();

  if (!css) {
    existingStyleTag?.remove();
    return;
  }

  if (existingStyleTag) {
    existingStyleTag.textContent = css;
    return;
  }

  const styleTag = document.createElement('style');
  styleTag.id = TENANT_UPLOADED_FONTS_STYLE_TAG_ID;
  styleTag.textContent = css;
  document.head.appendChild(styleTag);
};

export const syncUploadedFontsStyleTagSource = (
  source: string,
  ...fontGroups: Array<UploadedFontCssSource[] | null | undefined>
) => {
  const fonts = fontGroups.flatMap(fontGroup => fontGroup || []);

  if (fonts.length > 0) {
    uploadedFontsStyleSources.set(source, fonts);
  } else {
    uploadedFontsStyleSources.delete(source);
  }

  syncUploadedFontsStyleTag(...Array.from(uploadedFontsStyleSources.values()));
};

export const clearUploadedFontsStyleTagSource = (source: string) => {
  uploadedFontsStyleSources.delete(source);
  syncUploadedFontsStyleTag(...Array.from(uploadedFontsStyleSources.values()));
};
